import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

interface Column {
  label: string;
  subtitle: ReactNode;
  bullets: string[];
}

const COLUMNS: Column[] = [
  {
    label: "Insights &\nRecommendations",
    subtitle: (
      <>
        Data ingested automatically from 20+ sources then processed through
        proprietary{" "}
        <span className="font-semibold text-slate-900">ASO Engines</span> that
        embody human-curated ASO methodology.
      </>
    ),
    bullets: [
      "Live store listings",
      "User reviews & rankings",
      "Competitor movements",
      "AI/web visibility",
      "Keyword performance",
    ],
  },
  {
    label: "Actions",
    subtitle: (
      <>
        Review recommendations to build a draft revision.{" "}
        <span className="font-semibold text-slate-900">
          Most recommendations are actionable through one-click publishing
          directly inside Apptonomy.
        </span>
      </>
    ),
    bullets: [],
  },
  {
    label: "Impact",
    subtitle: (
      <>
        Source-of-truth performance data directly from the App Store and Google
        Play — quant KPIs tied directly to each published revision.
      </>
    ),
    bullets: [],
  },
];

export default function TheSolution() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-8">
      <SlideHeader eyebrow="The Solution">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          You require continuous on-trend ASO for all of your apps.
        </h1>
        <p className="mt-3 text-lg font-medium tracking-tight text-slate-700 md:mt-4 md:text-2xl">
          Not another ASO dashboard. Not a thin AI wrapper calling itself SaaS.
          You require…
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        {COLUMNS.map((col) => (
          <SolutionColumn key={col.label} {...col} />
        ))}
      </div>

      <div className="mt-3 text-center md:mt-auto">
        <p className="text-balance text-lg font-bold italic leading-snug text-violet-700 md:text-3xl">
          You require an ASO operating system designed for the future of app
          marketing in the AI age.
        </p>
      </div>
    </div>
  );
}

function SolutionColumn({ label, subtitle, bullets }: Column) {
  return (
    <div className="flex flex-col rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200 md:p-6">
      <h3 className="whitespace-pre-line text-xl font-bold leading-tight tracking-tight text-blue-600 md:text-3xl">
        {label}
      </h3>
      {subtitle ? (
        <p className="mt-1 text-base italic leading-snug text-slate-600 md:mt-2 md:text-xl">
          {subtitle}
        </p>
      ) : null}
      {bullets.length > 0 ? (
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-600 md:mt-3 md:text-base md:leading-snug">
          {bullets.map((b) => (
            <li key={b} className="flex gap-2">
              <span aria-hidden="true" className="shrink-0 text-blue-600">
                →
              </span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
